import React, { useState, useEffect, useCallback } from 'react';
import { Plus, MapPin, Monitor } from 'lucide-react';
import { listCities } from '../api/cityApi';
import { useCity } from '../contexts/CityContext';
import CreateCityModal from './CreateCityModal';

/**
 * Cities management page (super-admin). Lists every city and lets an admin add
 * a new one. Adding a city is a pure data operation — the new city reuses the
 * entire dashboard UI with its own isolated devices/content/schedules.
 */
export default function CitiesView() {
  const { reloadCities, currentCity } = useCity();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setCities(await listCities({ status: 'all' }));
    } catch (err) {
      setError(err.message || 'Failed to load cities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCreated = async () => {
    setShowCreate(false);
    await load();
    if (reloadCities) reloadCities(); // refresh the header selector too
  };

  return (
    <div style={{ maxWidth: 880, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Cities</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            Each city is an isolated tenant — its own devices, content, and schedules.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600, color: '#fff', background: '#c8a147', border: 'none', borderRadius: 10, cursor: 'pointer' }}
        >
          <Plus size={16} /> Add City
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: 'rgba(193,71,71,0.08)', border: '1px solid rgba(193,71,71,0.2)', borderRadius: 8, color: '#c14747', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 13 }}>Loading cities…</div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {cities.map((c) => {
            const active = c.projectId === currentCity;
            return (
              <div key={c.projectId}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', background: '#fff',
                  border: `1px solid ${active ? 'rgba(200,161,71,0.5)' : '#e2e8f0'}`, borderRadius: 12,
                }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(200,161,71,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <MapPin size={20} color="#c8a147" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>
                    {c.displayName}
                    {active && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#92400e', background: 'rgba(200,161,71,0.15)', padding: '2px 8px', borderRadius: 6 }}>current</span>}
                    {c.status === 'archived' && <span style={{ marginLeft: 8, fontSize: 11, color: '#64748b' }}>(archived)</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{c.projectId}</div>
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b' }}>
                  <Monitor size={15} /> {c.deviceCount ?? 0} screen{(c.deviceCount ?? 0) === 1 ? '' : 's'}
                </div>
              </div>
            );
          })}
          {cities.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 13 }}>No cities yet. Add your first city.</div>
          )}
        </div>
      )}

      {showCreate && <CreateCityModal onClose={() => setShowCreate(false)} onCreated={onCreated} />}
    </div>
  );
}
